import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';

import SOURCE_TYPE from '@/features/SourceCreate/SourceCreate.constants';
import { SourceDialogMode } from '@/features/SourceCreate/SourceCreate.types';

import type {
  ParsedAzureConnectionString,
  UseAzureConnectionStringParams
} from './CloudSourceHooks.types';

/**
 * Custom hook to handle Azure Event Hub connection string parsing and auto-population
 * of SAS token and expiry date fields.
 */
export const useAzureConnectionString = ({
  name,
  sourceTypeId,
  mode,
  storageConnectionString,
  setValue,
  trigger
}: UseAzureConnectionStringParams) => {
  // Track if connection string has been initialized in Edit mode
  const isInitialLoadRef = useRef<boolean>(true);

  // Helper function to validate and parse Azure connection string
  const parseAzureConnectionString = (
    connString: string
  ): ParsedAzureConnectionString | null => {
    try {
      // Check if it's a valid Azure connection string format
      if (!connString || typeof connString !== 'string') {
        return null;
      }

      // Connection string should contain SharedAccessSignature
      if (!connString.includes('SharedAccessSignature=')) {
        return null;
      }

      // Extract the SAS token (everything after SharedAccessSignature=)
      const sasMatch = connString.match(/SharedAccessSignature=([^;]*)/);
      if (!sasMatch?.[1]) {
        return null;
      }

      const sasToken = sasMatch[1];

      // Extract expiry date from SAS token (se parameter)
      const expiryMatch = sasToken.match(/se=([^&]*)/);
      if (!expiryMatch?.[1]) {
        return null;
      }

      const expiryString = decodeURIComponent(expiryMatch[1]);
      const expiryDate = dayjs(expiryString);

      // Validate the date is valid
      if (!expiryDate.isValid()) {
        return null;
      }

      return {
        sasToken,
        expiryDate
      };
    } catch {
      return null;
    }
  };

  // Reset initial load flag when component unmounts
  useEffect(() => {
    return () => {
      isInitialLoadRef.current = true;
    };
  }, []);

  // Auto-populate SAS token and expiry date when connection string changes (Azure source type only)
  useEffect(() => {
    // Only validate and auto-populate for Azure source type (source_type === AZURE_EVENT_HUB)
    if (
      name === 'storage_acct_conn_string' &&
      sourceTypeId === SOURCE_TYPE.AZURE_EVENT_HUB &&
      storageConnectionString &&
      storageConnectionString.trim() !== ''
    ) {
      // Skip auto-population on initial load in Edit mode
      if (mode === SourceDialogMode.Edit && isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
        if (trigger) {
          trigger('storage_acct_conn_string');
        }
        return;
      }

      const parsed = parseAzureConnectionString(storageConnectionString);

      if (parsed) {
        // Valid connection string - auto-populate fields
        setValue('sas_token', parsed.sasToken, { shouldValidate: false });
        setValue('expires', parsed.expiryDate, { shouldValidate: false });
      } else if (trigger) {
        // Invalid connection string - trigger validation to show error
        trigger('storage_acct_conn_string');
      }

      // Mark as no longer initial load after first change
      isInitialLoadRef.current = false;
    }
  }, [storageConnectionString, name, sourceTypeId, setValue, mode, trigger]);

  return {
    parseAzureConnectionString,
    isAzureSource: sourceTypeId === SOURCE_TYPE.AZURE_EVENT_HUB
  };
};

export default useAzureConnectionString;
